import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Download, RefreshCw, Sparkles } from "lucide-react";

/**
 * DesktopUpdater — friendly in-app update flow for the Electron desktop build.
 *
 * Bridges `window.electronUpdater` (see electron/preload.cjs) and
 * `updater:status` CustomEvents from the main process into:
 *   • a bottom-right progress toast while downloading
 *   • a modal "restart to update" prompt when the download completes
 *   • a manual "Check for updates" trigger callers can import
 *
 * In a plain browser this component is a no-op — all APIs are guarded on
 * `window.electronRuntime?.isElectron`.
 */

type UpdaterStatus =
  | { state: "checking" }
  | { state: "available"; version: string }
  | { state: "none"; currentVersion?: string }
  | { state: "downloading"; percent: number }
  | { state: "downloaded"; version: string; releaseNotes?: string | null }
  | { state: "error"; message: string }
  | { state: "dev" };

declare global {
  interface Window {
    electronUpdater?: {
      check: () => Promise<UpdaterStatus>;
      installNow: () => Promise<boolean>;
      getVersion: () => Promise<string>;
      isPackaged: () => Promise<boolean>;
    };
  }
}

const isDesktop = () =>
  typeof window !== "undefined" && !!window.electronRuntime?.isElectron;

/** Trigger a manual check. Safe to call from anywhere (browser: no-op). */
export async function checkForUpdatesNow(opts: { silent?: boolean } = {}) {
  if (!isDesktop() || !window.electronUpdater) {
    if (!opts.silent) toast.info("Updates are only available in the desktop app.");
    return;
  }
  if (!opts.silent) toast.loading("Checking for updates…", { id: "updater-check" });
  const result = await window.electronUpdater.check();
  if (opts.silent) return;
  toast.dismiss("updater-check");
  if (result.state === "dev") {
    toast.info("Updates only run in the packaged desktop app.");
  } else if (result.state === "none") {
    toast.success(
      `You're up to date${result.currentVersion ? ` (v${result.currentVersion})` : ""}`,
    );
  } else if (result.state === "available") {
    toast.success(`Update v${result.version} found — downloading in the background.`);
  } else if (result.state === "error") {
    toast.error(`Update check failed: ${result.message}`);
  }
}

export function DesktopUpdater() {
  const [downloadPct, setDownloadPct] = useState<number | null>(null);
  const [ready, setReady] = useState<{ version: string; notes?: string | null } | null>(
    null,
  );
  const [installing, setInstalling] = useState(false);
  const lastDownloadToast = useRef<string | number | null>(null);

  useEffect(() => {
    if (!isDesktop()) return;

    const handler = (e: Event) => {
      const s = (e as CustomEvent<UpdaterStatus>).detail;

      if (s.state === "available") {
        toast.info(`Update v${s.version} available — downloading…`, {
          icon: <Download className="h-4 w-4" />,
          duration: 4000,
        });
        setDownloadPct(0);
      } else if (s.state === "downloading") {
        const pct = Math.max(0, Math.min(100, Math.round(s.percent)));
        setDownloadPct(pct);
        // Persistent progress toast, updated in place
        lastDownloadToast.current = toast.loading(`Downloading update… ${pct}%`, {
          id: "updater-download",
        });
      } else if (s.state === "downloaded") {
        setDownloadPct(100);
        toast.dismiss("updater-download");
        setReady({ version: s.version, notes: s.releaseNotes });
      } else if (s.state === "error") {
        toast.dismiss("updater-download");
        toast.error(`Update failed: ${s.message}`, { duration: 6000 });
        setDownloadPct(null);
      }
    };

    window.addEventListener("updater:status", handler);
    return () => window.removeEventListener("updater:status", handler);
  }, []);

  if (!isDesktop()) return null;

  const handleRestart = async () => {
    setInstalling(true);
    try {
      await window.electronUpdater?.installNow();
    } catch (err) {
      toast.error(`Failed to install update: ${String(err)}`);
      setInstalling(false);
    }
  };

  return (
    <Dialog open={!!ready} onOpenChange={(open) => !open && setReady(null)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Update ready to install
          </DialogTitle>
          <DialogDescription>
            DoctorsX <span className="font-medium">v{ready?.version}</span> has been
            downloaded. Restart now to apply the update — your work is saved.
          </DialogDescription>
        </DialogHeader>

        {ready?.notes ? (
          <div className="max-h-40 overflow-y-auto rounded-md border bg-muted/40 p-3 text-sm">
            <pre className="whitespace-pre-wrap font-sans text-xs leading-relaxed text-muted-foreground">
              {ready.notes}
            </pre>
          </div>
        ) : null}

        {downloadPct !== null && downloadPct < 100 ? (
          <div className="space-y-1">
            <Progress value={downloadPct} className="h-2" />
            <p className="text-xs text-muted-foreground">Finalizing… {downloadPct}%</p>
          </div>
        ) : null}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => setReady(null)} disabled={installing}>
            Install on next launch
          </Button>
          <Button onClick={handleRestart} disabled={installing}>
            {installing ? (
              <>
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                Restarting…
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-4 w-4" />
                Restart & update
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
